import React, { useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, ArrowRight, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { useStatusCounts } from '@/hooks/useStatusCounts';
import { useRecentActivity } from '@/hooks/useRecentActivity';
import { StatCard } from '@/components/dashboard/StatCard';
import { Activity } from '@/components/dashboard/Activity';
import { ActiveSitesStatus } from '@/components/dashboard/ActiveSitesStatus';
import { Skeleton } from '@/components/ui/skeleton';
import Announcements from '@/components/dashboard/Announcements';
import { useUser } from '@/providers/UserProvider';
import { getStatusInfo, STATUS_COLORS } from '@/utils/submissions';

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
}) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const { color, label } = getStatusInfo(name);
  return (
    <div className="bg-gray-900 text-white rounded-md px-3 py-2 text-sm shadow-lg z-50 relative">
      <div className="flex items-center gap-2">
        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="font-medium">{label}</span>
      </div>
      <div className="mt-1 text-gray-300">
        {value}{' '}
        {value === 1
          ? t('dashboard.submission_singular')
          : t('dashboard.submission_plural')}
      </div>
    </div>
  );
};

interface ActivitiesListProps {
  isLoading: boolean;
  activities: ReturnType<typeof useRecentActivity>['activities'];
}

const ActivitiesList: FC<ActivitiesListProps> = ({ isLoading, activities }) => {
  const { t } = useTranslation();

  if (isLoading) {
    return Array.from({ length: 5 }, (_, i) => (
      <Skeleton key={i} className="h-[86px] w-full" />
    ));
  }

  if (activities.length) {
    return activities.map(activity => (
      <Activity key={activity.id} {...activity} />
    ));
  }

  return (
    <div className="text-center py-8 text-gray-500">
      <p>{t('dashboard.no_recent_activities')}</p>
    </div>
  );
};

const PIE_CHART_STATUSES: Array<{ status: string; isQuickFilter: boolean }> = [
  { status: 'response_required', isQuickFilter: true },
  { status: 'draft', isQuickFilter: false },
  { status: 'submitted', isQuickFilter: false },
  { status: 'pre_review', isQuickFilter: false },
  { status: 'irb_review', isQuickFilter: false },
  { status: 'post_review', isQuickFilter: false },
  { status: 'expiring_soon', isQuickFilter: true },
];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const { t } = useTranslation();
  const { statusCounts, isLoading: isLoadingStatusCounts } = useStatusCounts();
  const { activities, isLoading: isLoadingActivities } = useRecentActivity();
  const [activitySearch, setActivitySearch] = useState('');

  const handleCreateSubmission = () => {
    navigate('/submissions/new');
  };

  const pieChartData = useMemo(
    () =>
      PIE_CHART_STATUSES.map(({ status, isQuickFilter }) => ({
        status,
        isQuickFilter,
        value: statusCounts[status] || 0,
      })),
    [statusCounts]
  );

  const handlePieClick = (_: unknown, index: number) => {
    const { status, isQuickFilter } = pieChartData[index];
    const param = isQuickFilter ? 'quick_filter' : 'status';
    navigate(`/submissions?${param}=${status}`);
  };

  const filteredActivities = useMemo(() => {
    if (!activitySearch.trim()) return activities;
    const q = activitySearch.toLowerCase();
    return activities.filter(
      a =>
        a.title.toLowerCase().includes(q) || a.details.toLowerCase().includes(q)
    );
  }, [activities, activitySearch]);

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white sticky top-0 z-10">
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('dashboard.welcome', { name: userData?.first_name })}
        </h1>
        <Button
          onClick={handleCreateSubmission}
          className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-3 py-1.5 rounded-md flex items-center gap-2 transition-colors"
        >
          <FileText className="w-4 h-4" />
          <span>{t('dashboard.new_submission')}</span>
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
        {/* Submission Overview Section */}
        <div className="bg-white border border-gray-200 rounded-[10px] p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl text-gray-900">
              {t('dashboard.submission_overview')}
            </h2>
            <Link
              to="/submissions"
              className="flex items-center gap-1.5 px-0 py-2 text-sm font-medium text-primary hover:underline"
            >
              <span>{t('dashboard.view_all_submissions')}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Cards and Chart Layout */}
          <div className="flex flex-col lg:flex-row lg:gap-20 lg:items-end gap-6">
            {/* Stats Cards — single 2-col mobile / 3-col desktop grid */}
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-3">
              <StatCard
                label={t('dashboard.statuses.response_required')}
                value={statusCounts.response_required || 0}
                color={STATUS_COLORS.response_required}
                isLoading={isLoadingStatusCounts}
                status="response_required"
                isQuickFilter
              />
              <StatCard
                label={t('dashboard.statuses.drafts')}
                value={statusCounts.draft || 0}
                color={STATUS_COLORS.draft}
                isLoading={isLoadingStatusCounts}
                status="draft"
              />
              <StatCard
                label={t('dashboard.statuses.submitted')}
                value={statusCounts.submitted || 0}
                color={STATUS_COLORS.submitted}
                isLoading={isLoadingStatusCounts}
                status="submitted"
              />
              <StatCard
                label={t('dashboard.statuses.pre_review')}
                value={statusCounts.pre_review || 0}
                color={STATUS_COLORS.pre_review}
                isLoading={isLoadingStatusCounts}
                status="pre_review"
              />
              <StatCard
                label={t('dashboard.statuses.irb_review')}
                value={statusCounts.irb_review || 0}
                color={STATUS_COLORS.irb_review}
                isLoading={isLoadingStatusCounts}
                status="irb_review"
              />
              <StatCard
                label={t('dashboard.statuses.post_review')}
                value={statusCounts.post_review || 0}
                color={STATUS_COLORS.post_review}
                isLoading={isLoadingStatusCounts}
                status="post_review"
              />
              <StatCard
                label={t('dashboard.statuses.expiring_soon')}
                value={statusCounts.expiring_soon || 0}
                color={STATUS_COLORS.expiring_soon}
                isLoading={isLoadingStatusCounts}
                status="expiring_soon"
                isQuickFilter
              />
            </div>

            {/* Donut Chart */}
            <div className="flex-shrink-0 w-[246px] h-[246px] relative mx-auto lg:mx-0">
              <PieChart width={246} height={246} style={{ outline: 'none' }}>
                <Pie
                  data={pieChartData.map(({ status, value }) => ({
                    name: status,
                    value,
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={61}
                  outerRadius={123}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  stroke="none"
                  strokeWidth={0}
                  activeShape={undefined}
                  onClick={handlePieClick}
                  style={{ cursor: 'pointer', outline: 'none' }}
                >
                  {pieChartData.map(({ status }, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getStatusInfo(status).color}
                      strokeWidth={0}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltip />}
                  wrapperStyle={{ outline: 'none', zIndex: 10 }}
                  cursor={false}
                />
              </PieChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-0">
                <span className="text-sm text-[#535862]">
                  {t('dashboard.total')}
                </span>
                <span className="text-xl text-[#181d27]">
                  {pieChartData.reduce((sum, { value }) => sum + value, 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Active Site Status Section */}
        <ActiveSitesStatus />

        {/* Recent Activity and News Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Activity */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-[10px] p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
              <h2 className="text-xl text-gray-900">
                {t('dashboard.recent_activity')}
              </h2>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('dashboard.search_placeholder')}
                  className="w-full pl-9 pr-4 py-2 text-sm"
                  value={activitySearch}
                  onChange={e => setActivitySearch(e.target.value)}
                />
              </div>
            </div>

            <div className="max-h-[400px] sm:h-[600px] overflow-y-auto pr-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              <div className="divide-y divide-gray-200">
                <ActivitiesList
                  isLoading={isLoadingActivities}
                  activities={filteredActivities}
                />
              </div>
            </div>
          </div>

          {/* News & Notes */}
          <Announcements />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
